import {
  BorderOutlined,
  CheckSquareOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import ButtonComponent from "components/button/Button";
import SelectComponent from "components/select/Select";
import dayjs from "dayjs";
import { 
  apiExportRecordsCsvAuto,
  apiExportRecordsCsvManual,
  apiExportRecordsPdf,
  apiExportCSVAllDataForPatient,
} from "helpers/api/records";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BEDWETTING_TYPE_VALUE,
  BEDWETTING_TYPE_VALUE_MAPPING,
  MEMO_TYPE,
} from "../constants";
import "./styles.scss";
import Loading from "components/loading/Loading";

const CalendarInfo = ({
  selectData,
  dateSelected,
  setDateSelected,
  fetchData,
  patientId,
  treatmentDate
}) => {
  const { t } = useTranslation();
  const [exportType, setExportType] = useState();
  const [isLoading, setIsLoading] = useState();

  const getDiffDate = () => {
    var convertDateSelected = dayjs(dateSelected.format('YYYY/MM/DD'));
    var diff = convertDateSelected.diff(treatmentDate, 'd', true);
    if (diff == undefined || diff == null || isNaN(diff) || diff < 0) {
      return 0
    }
    return diff + 1;
  }

  const EXPORT_OPTIONS_VALUE = {
    PDF: 0,
    CSV_MANUAL: 1,
    CSV_AUTO: 2,
    CSV_ALL_DATA_MANUAL: 3,
    CSV_ALL_DATA_AUTO: 4,
  };

  const EXPORT_OPTIONS = () => {
    return window.location.pathname.includes("/clinic/")
      ? [
        {
          label: t("graph.calendarPage.calendarAndGraphs"),
          value: EXPORT_OPTIONS_VALUE.PDF,
          prefix: "/icons/pdf.webp",
        },
        {
          label: t("graph.calendarPage.userBedwettingForMonth", {
            month: dateSelected.month() + 1,
          }),
          value: EXPORT_OPTIONS_VALUE.CSV_AUTO,
          prefix: "/icons/csv.webp",
        },
      ]
      : [
        {
          label: t("graph.calendarPage.calendarAndGraphs"),
          value: EXPORT_OPTIONS_VALUE.PDF,
          prefix: "/icons/pdf.webp",
        },
        {
          label: t("graph.calendarPage.userManualForMonth", {
            month: dateSelected.month() + 1,
          }),
          value: EXPORT_OPTIONS_VALUE.CSV_MANUAL,
          prefix: "/icons/csv.webp",
        },
        {
          label: t("graph.calendarPage.userBedwettingForMonth", {
            month: dateSelected.month() + 1,
          }),
          value: EXPORT_OPTIONS_VALUE.CSV_AUTO,
          prefix: "/icons/csv.webp",
        },
        {
          label: t("graph.calendarPage.allDataUserManual"),
          value: EXPORT_OPTIONS_VALUE.CSV_ALL_DATA_MANUAL,
          prefix: "/icons/csv.webp",
        },
        {
          label: t("graph.calendarPage.allDataUserAuto"),
          value: EXPORT_OPTIONS_VALUE.CSV_ALL_DATA_AUTO,
          prefix: "/icons/csv.webp",
        },
      ];
  };

  const handlePrevMonth = () => {
    if (dateSelected.date() === 1) {
      fetchData(dateSelected.subtract(1, "months"));
    }
    setDateSelected((curent) => curent.subtract(1, "day"));
  };

  const handleNextMonth = () => {
    if (dateSelected.date() === dateSelected.endOf("month").date()) {
      fetchData(dateSelected.add(1, "months"));
    }
    setDateSelected((curent) => curent.add(1, "day"));
  };

  const handleExport = async () => {
    setIsLoading(true);
    const from = dayjs(dateSelected)
      .startOf("month")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");
    const to = dayjs(dateSelected)
      .endOf("month")
      .endOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");

    let res = {};

    switch (exportType) {
      case EXPORT_OPTIONS_VALUE.PDF:
        res = await apiExportRecordsPdf(patientId, { from, to });
        break;
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        res = await apiExportRecordsCsvManual({ from, to, patient_id: patientId });
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        res = await apiExportRecordsCsvAuto({ from, to, patient_id: patientId });
        break;
      case EXPORT_OPTIONS_VALUE.CSV_ALL_DATA_MANUAL:
        res = await apiExportCSVAllDataForPatient(patientId, 'manual');
        break;
      case EXPORT_OPTIONS_VALUE.CSV_ALL_DATA_AUTO:
        res = await apiExportCSVAllDataForPatient(patientId, 'auto');
        break;
      default:
        break;
    }

    if (res.data) {
      window.location.href = res.data;
    }
    setIsLoading(false);
    return;
  };

  const currentDate = dayjs(dateSelected);

  const displaySleepTime = (selectData) => {
    if (selectData?.sleep_time) {
      return dayjs(selectData?.sleep_time).format("HH : mm");
    }
    if (selectData?.sleep_time_sensor) {
      return dayjs(selectData?.sleep_time_sensor).format("HH : mm");
    }
    return null;
  }

  const displayWakeUpTime = (selectData) => {
    if (selectData?.wakeup_time) {
      return dayjs(selectData?.wakeup_time).format("HH : mm");
    }
    if (selectData?.wakeup_time_sensor) {
      return dayjs(selectData?.wakeup_time_sensor).format("HH : mm");
    }
    return null;
  }

  const displayBetWettingTime = (selectData) => {
    if (selectData?.bed_wetting_time) {
      return dayjs(selectData?.bed_wetting_time).format("HH : mm");
    }
    if (selectData?.bed_wetting_time_sensor) {
      return dayjs(selectData?.bed_wetting_time_sensor).format("HH : mm");
    }
    return null;
  }

  const displayMemoTime = (selectData) => {
    if (selectData?.memo_time) {
      return dayjs(selectData?.memo_time).format("HH : mm");
    }
    if (selectData?.memo_time_sensor) {
      return dayjs(selectData?.memo_time_sensor).format("HH : mm");
    }
    return null;
  }

  return (
    <div className="calendar-info-container">
      <div className="home-right-info">
        <div className="home-right-info-date">
          <LeftOutlined
            className="home-right-info-date-icon"
            onClick={handlePrevMonth}
          />
          {`${currentDate.format("YYYY年M月D日")}(${t(
            `graph.dayOfWeek.${currentDate.format("d")}`
          )}) ${getDiffDate() + "日目"}`}
          <RightOutlined
            className="home-right-info-date-icon"
            onClick={handleNextMonth}
          />
        </div>
        <div className="home-right-info-item">
          <img src="/icons/star.webp" className="icon" />
          <div className="home-right-info-item-content">
            {t("graph.calendarPage.timeYouSleep")}
          </div>
          <div className="home-right-info-item-value">
            {displaySleepTime(selectData)}
          </div>
        </div>
        <div className="home-right-info-medication">
          <div
            className="home-right-info-item"
            style={{ borderBottom: "none" }}
          >
            <img src="/icons/medicine.webp" className="icon" />
            <div className="home-right-info-item-content">
              {t("graph.calendarPage.useMedication")}
            </div>
            <div className="home-right-info-item-value">
              <div
                className={`label-yes-no ${selectData?.is_take_medication === true ? "label-yes-no-active" : ""
                  }`}
              >
                {t("graph.calendarPage.can")}
              </div>
              <div
                className={`label-yes-no ${selectData?.is_take_medication === false ? "label-yes-no-active" : ""
                  }`}
              >
                {t("graph.calendarPage.canNot")}
              </div>
            </div>
          </div>
          <div className="list-medication">
            {selectData?.medications?.map((medi) => (
              <div className="medication-item">{medi}</div>
            ))}
          </div>
        </div>
        <div className="home-right-info-item">
          <img src="/icons/waterlevel.webp" className="icon" />
          <div className="home-right-info-item-content">
            {t("graph.calendarPage.amountDrinkBeforeSleep")}
          </div>
          <div className="home-right-info-item-value-right">
            {selectData?.amount_water_before_bed} ㎖
          </div>
        </div>
        <div className="home-right-info-item">
          <img src="/icons/peeNight.svg" className="icon" />
          <div className="home-right-info-item-content">
            {t("graph.calendarPage.isBedwetting")}
          </div>
          <div className="home-right-info-item-value">
            <div
              className={`label-yes-no ${selectData?.is_bed_wetting === true ? "label-yes-no-active" : ""
                }`}
            >
              {t("graph.calendarPage.can")}
            </div>
            <div
              className={`label-yes-no ${selectData?.is_bed_wetting === false ? "label-yes-no-active" : ""
                }`}
            >
              {t("graph.calendarPage.canNot")}
            </div>
          </div>
        </div>
        <div className="home-right-info-item-time">
          <div className="home-right-info-item">
            <img src="/icons/alarm.webp" className="icon" />
            <div className="home-right-info-item-content">
              {t("graph.calendarPage.bedwettingTime")}
            </div>
            <div className="home-left-info-item-value">
              {displayBetWettingTime(selectData) != null
                ? displayBetWettingTime(selectData)
                : Object.keys(selectData).length > 0 ? t("graph.calendarPage.noBedwettingToMorning") : ''}

            </div>
          </div>
          <div className="alarm-action">
            {t(
              BEDWETTING_TYPE_VALUE[
                BEDWETTING_TYPE_VALUE_MAPPING[selectData?.bed_wetting_type]
              ]?.title
            )}
          </div>
          <div className="alarm-action">
            {selectData?.is_bed_wetting_to_toilet ? (
              <CheckSquareOutlined />
            ) : (
              <BorderOutlined />
            )}&nbsp;
            {t("graph.calendarPage.toiletAfterBedwetting")}
          </div>
        </div>
        <div className="home-right-info-item">
          <img src="/icons/waterlevelnight.webp" className="icon" />
          <div className="home-right-info-item-content">
            {t("graph.calendarPage.bedwettingAmount")}
          </div>
          <div className="home-right-info-item-value">
            {selectData?.bed_wetting_amount} g
          </div>
        </div>
        <div className="home-right-info-item">
          <img src="/icons/wakeuptime.webp" className="icon" />
          <div className="home-right-info-item-content">
            {t("graph.calendarPage.wakeUpTime")}
          </div>
          <div className="home-right-info-item-value">
            {displayWakeUpTime(selectData)}
          </div>
        </div>
        <div className="home-right-info-item">
          <img src="/icons/waterlevelafterwakeup.webp" className="icon" />
          <div className="home-right-info-item-content">
            {t("graph.calendarPage.urineAfterWakingUp")}
          </div>
          <div className="home-right-info-item-value">
            {selectData?.urine_amount} ml
          </div>
        </div>
        <div className="home-right-info-memo">
          <div className="home-right-info-memo-left">
            <img src="/icons/memo.webp" className="icon" />
            <div className="home-right-info-memo-text">
              {t("graph.calendarPage.note")}
            </div>
          </div>
          <div className="home-right-info-memo-right" style={{ width: '90%' }}>
            <div className="memo-row">
              <div className="memo-input" style={{ border: 'none' }}>
                {selectData?.is_water_after_bed ? (
                  <CheckSquareOutlined />
                ) : (
                  <BorderOutlined />
                )}&nbsp;
                {t("graph.calendarPage.hasDrinkWater")}


              </div>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div className="memo-value" style={{ textAlign: 'center' }}>
                  {selectData?.amount_water_after_bed}
                </div>
                <div className="memo-unit">ml</div>
              </div>
            </div>
            <div className="memo-row mt-10">
              <div className="memo-input expanded">
                {!selectData?.is_bed_wetting
                  ? t(MEMO_TYPE[selectData.memo_type])
                  : ""}
              </div>
              <div className="memo-time ">
                {!selectData?.is_bed_wetting
                  ? displayMemoTime(selectData)
                  : null}
              </div>
            </div>

            <div className="memo-row">
              <div className="memo-note expanded" >{selectData?.memo || ""}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="export-section">
        <SelectComponent
          options={EXPORT_OPTIONS()}
          popupClassName="export-section-options"
          onChange={(e) => setExportType(e)}
          optionRender={(option) => {
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: "bold",
                }}
                className="options-hover"
              >
                <img
                  src={option?.data?.prefix}
                  width="30px"
                  style={{ marginRight: "10px" }}
                />
                {option.label}
              </div>
            );
          }}
        />
        <ButtonComponent
          background="#035697"
          className="export-button"
          text={t("graph.calendarPage.export")}
          type="button"
          onClick={handleExport}
        />
      </div>
      {isLoading && <Loading />}
    </div>
  );
};

export default CalendarInfo;
